/**
 * Personal Info Step - Step 1 of Onboarding
 */

import React, { useState } from 'react';
import { Button, Input } from '../common';

interface PersonalInfoStepProps {
  initialData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
  };
  onNext: (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }) => void;
  isLoading?: boolean;
}

export const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  initialData,
  onNext,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    email: initialData?.email || '',
    password: initialData?.password || '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
      });
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-bold text-pos-dark mb-2">
          Welcome to SmartPOS
        </h2>
        <p className="text-pos-secondary">
          Let&apos;s get started with your account
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            value={formData.firstName}
            onChange={(val) =>
              setFormData({ ...formData, firstName: val.toString() })
            }
            error={errors.firstName}
            placeholder="John"
            required
          />
          <Input
            label="Last Name"
            type="text"
            value={formData.lastName}
            onChange={(val) =>
              setFormData({ ...formData, lastName: val.toString() })
            }
            error={errors.lastName}
            placeholder="Doe"
            required
          />
        </div>

        <Input
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(val) =>
            setFormData({ ...formData, email: val.toString() })
          }
          error={errors.email}
          placeholder="john@example.com"
          required
        />

        <Input
          label="Password"
          type="password"
          value={formData.password}
          onChange={(val) =>
            setFormData({ ...formData, password: val.toString() })
          }
          error={errors.password}
          placeholder="Min. 8 characters"
          helperText="Use a strong password with letters, numbers, and symbols"
          required
        />

        <Input
          label="Confirm Password"
          type="password"
          value={formData.confirmPassword}
          onChange={(val) =>
            setFormData({ ...formData, confirmPassword: val.toString() })
          }
          error={errors.confirmPassword}
          placeholder="Re-enter password"
          required
        />

        <Button type="submit" fullWidth size="lg" isLoading={isLoading}>
          Continue
        </Button>
      </form>
    </div>
  );
};
